import z from 'zod';

/**
 * Represents any valid JSON primitive value.
 */
export type JSONPrimitive = string | number | boolean | null;

/**
 * Represents any valid JSON value (recursive).
 */
export type JSONValue = JSONPrimitive | JSONObject | JSONArray;

/**
 * A JSON object with string keys and JSON values.
 */
export interface JSONObject {
  [key: string]: JSONValue;
}

/**
 * A JSON array of JSON values.
 */
export type JSONArray = JSONValue[];

/**
 * JSONB type for use with Drizzle `$type<JSONB>()` annotations.
 * Supports both objects and arrays at the top level.
 * For entity-specific JSONB columns, prefer domain types (e.g. CustomRibbonFilters).
 */
export type JSONB = JSONObject | JSONArray;

/**
 * Zod schema for validating JSONB values (arrays or objects at the top level).
 * Nested values are validated recursively against valid JSON types.
 */
export const jsonValueSchema: z.ZodType<JSONValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number().finite(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema),
  ])
);

export const jsonbSchema: z.ZodType<JSONB> = z.union([
  z.array(jsonValueSchema),
  z.record(z.string(), jsonValueSchema),
]);

/**
 * Allowed value types for a single custom attribute field.
 *
 * - `string` — Text, Textarea, Date, Link, Select
 * - `number` — supported by external API
 * - `boolean` — supported by external API
 * - `string[]` — MultiSelect, UserMultiSelect, DepartmentMultiSelect, Tags
 * - `null` — unset/empty field
 */
export type CustomAttributeValue = string | number | boolean | string[] | null;

/**
 * Custom attribute data stored on entities. A flat key-value map where keys
 * are custom field identifiers (e.g. `controlType__fieldName`) and values
 * are user-entered data constrained to {@link CustomAttributeValue}.
 */
export type CustomAttributeData = Record<string, CustomAttributeValue>;

const customAttributeValueSchema = z.union([
  z.string(),
  z.number().finite(),
  z.boolean(),
  z.array(z.string()),
  z.null(),
]);

/**
 * Zod schema for CustomAttributeData. Validates a string-keyed object with
 * values restricted to the supported custom field value types.
 *
 * Keys whose value is `undefined` are stripped before validation: an unset
 * custom-attribute field is semantically "not provided". Forms submit blank
 * fields as `undefined`, and unlike the legacy GraphQL path (where
 * `JSON.stringify` dropped them), the tRPC + superjson transport preserves
 * `undefined` over the wire — so they must be filtered here rather than
 * failing the value union (RSP-5940). Explicit `null` is preserved as a
 * deliberate "cleared" marker.
 */
export const customAttributeDataSchema: z.ZodType<
  CustomAttributeData,
  z.ZodTypeDef,
  unknown
> = z.preprocess(
  (val) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      // Narrowed to a non-array object above; each value is validated below.
      const record = val as Record<string, unknown>;

      return Object.fromEntries(
        Object.entries(record).filter(([, value]) => value !== undefined)
      );
    }

    return val;
  },
  z.record(z.string(), customAttributeValueSchema)
);

export const auditFieldSchema = z.object({
  createdByUser: z.string(),
  modifiedByUser: z.string(),
  createdAtTimestamp: z.date(), // todo should these be strings? node is a bit inconsistent with date handling
  modifiedAtTimestamp: z.date(),
});

/**
 * Optimistic-concurrency lock token carried by every V3 update/delete
 * surface. Must be a full ISO 8601 datetime; `offset: true` accepts both
 * the drizzle customType emission (`2026-07-29T09:44:54.543829Z`) and the
 * Hasura/Apollo leg (`2026-07-29T09:44:54.543829+00:00`). No `precision`
 * option, so second / milli / microsecond fractions all pass.
 *
 * Format-validating here (V2 parity) turns a malformed token into a 400 at
 * the schema boundary instead of a Postgres `22007 invalid input syntax`
 * 500 inside `optimisticLockWhere` (COI-668).
 *
 * Which wrapper a given update surface uses is a pinned rule (COI-673):
 * the token is `originalTimestampNullishSchema` iff
 *
 *   (a) the entity's update is reachable through change-request replay and
 *       stored request blobs may predate the token — the merge consumer
 *       replays the persisted payload verbatim, so a blob written before
 *       the field existed must still apply (RSP-5853); or
 *   (b) the entity's V2 input declared the token nullable (e.g.
 *       `UpdateTestResultInput.OriginalTimestamp: timestamptz` — no `!`),
 *       so requiring it would reject payloads V2 accepted; or
 *   (c) the site was already nullish for an entity whose V2 surface had no
 *       token at all — grandfathered, because tightening it would break
 *       existing token-less callers.
 *
 * Otherwise the token is required (`originalTimestampSchema`): V2-required
 * parity sites, and entities whose lock V3 introduced (COI-624) where every
 * caller has always sent it. The classification is enforced per-procedure by
 * `packages/trpc/src/routers/original-timestamp-classification.test.ts` —
 * changing a site's wrapper without reclassifying it there is a test
 * failure.
 */
export const originalTimestampSchema = z.string().datetime({
  offset: true,
  message: 'OriginalTimestamp must be an ISO 8601 datetime',
});

/**
 * Nullish variant for surfaces where the lock token may be legitimately
 * omitted — CR-replay-reachable entities, V2-nullable inputs, and
 * grandfathered relaxations (see the rule on `originalTimestampSchema`). An
 * omitted token makes the data-layer skip the lock; explicit null represents
 * a caller snapshot of a never-modified row and locks on
 * `ModifiedAtTimestamp IS NULL` (`optimisticLockWhere`).
 */
export const originalTimestampNullishSchema = originalTimestampSchema.nullish();

/**
 * PostgreSQL `integer` (int4) range. Any value outside it is a
 * `22003 numeric value out of range` from the driver, so bounding an otherwise
 * unconstrained integer column here keeps that a 400 instead of a 500 — the
 * COI-668 rule. Not a product bound: it is the column's physical capacity.
 */
const PG_INT4_MIN = -2147483648;
const PG_INT4_MAX = 2147483647;

/**
 * Rating for the internal-audit / oversight document+obligation results, whose
 * tables carry no CHECK constraint at all — deliberately NOT the floor-only
 * band of {@link unboundedPositiveRatingSchema}, even though they hold the same
 * kind of value. `{document,obligation}_internal_audit_result` are created with
 * a plain `"Rating" INTEGER`
 * (`1750675590556_segregate-risk-ratings-to-ia/up.sql:508`, `:628`), and the
 * oversight pair was renamed from the second-line tables created the same way
 * (`1750849742690_segregate-risk-ratings-to-second-line`, which contains no
 * CHECK). Bounding them would reject writes V2 accepted AND stored — the exact
 * breakage COI-650 set out to prevent.
 *
 * Required, not nullish: V2's four document and obligation internal-audit and
 * oversight result schemas all declared `Rating: z.number().int({ message:
 * 'Required' })` with no `nullable`, so a NULL-rated finding was rejected.
 *
 * Unbounded as a *rating*: no CHECK constrains the band, so any rating V2
 * accepted still persists — including 0 and values above 5, which an org's
 * internal-audit or oversight scale may legitimately use. The only bound is the
 * int4 range of the column itself, so an absurd value is a 400 here rather than
 * a Postgres out-of-range 500.
 */
export const unboundedResultRatingSchema = z
  .number()
  .int()
  .min(PG_INT4_MIN)
  .max(PG_INT4_MAX);

/**
 * The three effectiveness ratings — `DesignEffectiveness`,
 * `PerformanceEffectiveness`, `OverallEffectiveness` — on all three
 * control-test result tables: `test_result`,
 * `control_test_internal_audit_result` and `control_test_oversight_result`.
 *
 * **No ceiling.** The scale behind these columns is org-configurable taxonomy
 * (`taxonomy_org.taxonomy.Rating.effectiveness`, replaced wholesale by an org's
 * override), so a fixed ceiling rejects an option the org has legitimately
 * configured. COI-991 removed a `0-4` band for exactly that reason: an org
 * adding a 6th effectiveness option could not save a control test using it.
 *
 * **Floor of 0, deliberately kept.** Only the ceiling was the bug. The scale is
 * 0-based and grows upward, so no configured option is ever negative — and V2
 * rejected negatives at its own edge (`TestResultSharedSchema` was
 * `.min(0).max(4)`), so a floor is V2 parity rather than a V3 narrowing. This
 * mirrors CUS-464, which replaced `cause.Significance`'s fixed `1-5` CHECK with
 * a floor-only `>= 1` instead of dropping it: widening a taxonomy is not the
 * same as admitting nonsense. Contrast
 * {@link unboundedResultRatingSchema}, whose columns genuinely have no CHECK in
 * any direction.
 *
 * The floor mirrors the DB on all three tables, so a negative is a 400 here
 * rather than a `23514 check_violation` 500:
 * - `test_result` carried three *validated* `0-4` checks from the squashed
 *   baseline (`1709744128112_squashed/up.sql:451`, `:459`, `:467`). COI-991's
 *   migration replaced them with floor-only `>= 0` checks.
 * - The internal-audit and oversight tables had none — both declare all three as
 *   a plain `INTEGER` (`1750675590556_segregate-risk-ratings-to-ia/up.sql:746`,
 *   `1750849742690_segregate-risk-ratings-to-second-line`). COI-991's migration
 *   adds the same floor-only checks so the family agrees.
 *
 * The int4 ceiling is the column's physical capacity, not a product bound — the
 * COI-668 rule: an absurd value is a 400 at the edge rather than a Postgres
 * `22003` 500.
 */
export const unboundedEffectivenessRatingSchema = z
  .number()
  .int()
  .min(0)
  .max(PG_INT4_MAX);

/**
 * Nullish variant of {@link unboundedEffectivenessRatingSchema}, for the write
 * schemas: all three columns are nullable, an unrated control test is legal, and
 * on a partial update an absent value means "leave unchanged".
 *
 * The non-nullish base is the one to use where optionality is itself part of a
 * published contract (the REST/OpenAPI response schema, the CSV importer) — there
 * `.nullable()` and `.nullish()` are not interchangeable.
 */
export const unboundedEffectivenessRatingNullishSchema =
  unboundedEffectivenessRatingSchema.nullish();

/**
 * Rating for a column whose CHECK is floor-only because the taxonomy behind it
 * is org-configurable. Three columns share this shape:
 *
 * - `cause.Significance` — CUS-464 replaced its fixed `IN (1,2,3,4,5)` CHECK
 *   with `>= 1`, because an org that adds a sixth significance option could not
 *   save a cause carrying it.
 * - `document_assessment_result.Rating` and
 *   `obligation_assessment_result.Rating` — COI-976 did the same to the
 *   `rating_check IN (1,2,3,4,5)` both tables carried from the squashed
 *   baseline (`1709744128112_squashed/up.sql:26836` and `:26876`). The scale
 *   behind them is `taxonomy_org.taxonomy.Rating.performance_result`, replaced
 *   wholesale by an org's override, so a fixed ceiling rejects an option the
 *   org has legitimately configured — GMP's scale runs 1–11.
 *
 * The two assessment-result tables moved together deliberately. They are the
 * only two that carried this CHECK, and they shared one Zod schema; widening
 * one alone would have flipped the other from a clean 400 to a Postgres
 * `23514 check_violation` 500.
 *
 * **Only the ceiling was ever the defect.** These scales are 1-based and grow
 * upward, so no configured option is 0 or negative, and the DB has never
 * allowed them — keeping the floor is parity, not a V3 narrowing.
 *
 * That parity rests on the DB alone for the two assessment-result columns, not
 * on V2's edge: `packages/rest-api`'s Obligation/DocumentAssessmentResultSchema
 * declare `Rating` as a bare `z.number().int()` with no `.min()`, so V2 accepted
 * 0 and negatives at the boundary and the VALIDATED CHECK refused to store them.
 * `cause.Significance` (CUS-464) is the same shape. Do not restate this as "V2
 * rejected it too" — that is true of
 * {@link unboundedEffectivenessRatingSchema} and false here.
 *
 * Widening a taxonomy is not the same as admitting nonsense. Contrast
 * {@link unboundedEffectivenessRatingSchema}, whose scale is 0-based and so
 * keeps a floor of 0 instead.
 *
 * Not a variant of {@link unboundedResultRatingSchema}: that one bounds only the
 * int4 range, admitting 0 and negatives, which these columns' CHECKs reject.
 *
 * The floor of 1 mirrors the DB CHECK on all three columns, so a 0 or negative
 * is a 400 here rather than a `23514 check_violation` 500. There is deliberately
 * no product ceiling — the taxonomy has no fixed size — so the only ceiling is
 * the int4 range of the column itself, the COI-668 rule: an absurd value is a
 * 400 here rather than a Postgres out-of-range 500.
 *
 * Beware `packages/drizzle/src/schema/`: it is a hand-maintained mirror (see
 * `packages/drizzle/CLAUDE.md`), not introspected output, and COI-698 found it
 * declaring four `rating_check`s that do not exist in the database at all.
 * Trust the migration DDL.
 */
export const unboundedPositiveRatingSchema = z
  .number()
  .int()
  .min(1)
  .max(PG_INT4_MAX);

/**
 * Nullish variant of {@link unboundedPositiveRatingSchema}, for the
 * document/obligation assessment-result update schemas.
 *
 * `null` and "absent" are NOT equivalent on the update path, and the difference
 * is destructive:
 * - **Absent** (`undefined`) leaves the stored Rating alone. Drizzle's `.set()`
 *   ignores `undefined` keys.
 * - **Explicit `null` clears the column.** Nothing between the edge and the
 *   table strips it — the update processor spreads the payload through
 *   (`const { OriginalTimestamp, ...values } = updatePayload`) and the
 *   repository spreads it again into `.set({ ...values })`, which writes NULL.
 *
 * That is legal — the column is nullable and an unrated result is a real state
 * — but a caller that sends `null` meaning "don't touch it" will wipe the
 * rating. Accepting `null` here is what makes clearing possible at all; do not
 * describe it as "leave unchanged".
 *
 * Creates use the non-nullish base, but that is a product rule rather than a DB
 * one: the columns are nullable (the squashed baseline's "Make ratings nullable"
 * block, `1709744128112_squashed/up.sql:28679` and `:28682`). V2 required the
 * value on every document/obligation result insert, so the create schemas keep
 * requiring it — do not relax that on the belief that a DB `NOT NULL` is
 * backing it.
 */
export const unboundedPositiveRatingNullishSchema =
  unboundedPositiveRatingSchema.nullish();

/**
 * Viewer-array input fragments for the V3 tRPC boundary (COI-675).
 *
 * The data-layer declares every viewer array `.optional()` — it has
 * no `.nullish()` counterpart anywhere (all 52 declarations were checked: 30
 * in `services/data-layer/src/schemas/`, 22 in the per-entity create/update
 * processors). An explicit `null` therefore fails data-layer validation, so
 * before this fragment a caller sending `ViewerUserIds: null` 400'd deep in
 * the data-layer instead of at the tRPC edge. Web hooks type these
 * `string[] | null`, so `null` is client-reachable — though the canonical form
 * helper emits `[]` rather than `null`, which is why this went unnoticed.
 *
 * The escape route that made it bite is the change-request blob, not the
 * direct-apply call. `executeAsyncRequest` passes the RAW input to `apiCall`
 * (`clients/async-request.ts:154`) — the services' `?? undefined` sits inside
 * `buildRequestBody`, which only feeds the request-state audit record
 * (`:135`), so direct-apply did carry the `null` to the data-layer. Worse, on
 * the approval-gated branch the same `null` reached `stripUndefinedJsonb`,
 * which strips `undefined` but KEEPS `null`, persisting `ViewerUserIds: null`
 * into `change_request.RequestedChanges` — where the merge replay hands it
 * back to the same `.optional()` schema and the CR can never be applied.
 * Collapsing here is what stops new blobs acquiring that shape.
 *
 * `.nullish()` keeps the inbound contract (callers may send `null`, omit the
 * key, or send an array); the transform collapses `null` to `undefined` so
 * only the three states the data-layer understands cross the wire. Those
 * states are the merged tri-state contract in
 * `services/data-layer/src/repositories/relationship-sync.ts`:
 * `undefined` = preserve, `[]` = clear, non-empty = exact set. Collapsing
 * `null` to `undefined` (= preserve) removes no clear path — clears travel
 * as `[]`.
 *
 * Normalising in the *input schema* rather than per-literal is deliberate:
 * only a minority of routers build an explicit object literal from
 * `req.input`; the rest forward it verbatim, where a `?? undefined` at the
 * call site has nothing to attach to. One declaration of the rule, reused
 * everywhere.
 */
export const viewerUserIdsSchema = z
  .array(z.string())
  .nullish()
  .transform((v) => v ?? undefined);

/** Group counterpart of `viewerUserIdsSchema` — see its rationale. */
export const viewerGroupIdsSchema = z
  .array(z.string().uuid())
  .nullish()
  .transform((v) => v ?? undefined);
